import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { 
  Package,
  Plus,
  Edit,
  Trash2,
  Image,
  DollarSign,
  Calendar,
  Grid3x3,
  Crown,
  Star,
  Award,
  Upload
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [matrices, setMatrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: 10.0,
    credits: 1000,
    matrix_config_id: '',
    lifespan_days: 30,
    tier_level: 1,
    phase_requirement: 1,
    prerequisites: [],
    graphics_url: '',
    features: [],
    is_active: true
  });

  const tierLevels = [
    { value: 1, label: 'Entry Level', description: 'Basic training with PIF', icon: Award, color: 'bg-gray-100 text-gray-800' },
    { value: 2, label: 'Rookie Pro', description: 'Phase 2 Level 1', icon: Star, color: 'bg-blue-100 text-blue-800' },
    { value: 3, label: 'Professional', description: 'Phase 2 Level 2', icon: Crown, color: 'bg-purple-100 text-purple-800' },
    { value: 4, label: 'Enterprise', description: 'Phase 2 Level 3', icon: Crown, color: 'bg-yellow-100 text-yellow-800' }
  ];

  const phaseRequirements = [
    { value: 1, label: 'Phase 1', description: 'No PIF requirement' },
    { value: 2, label: 'Phase 2', description: 'Requires 12 PIF completions' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, matricesRes] = await Promise.all([
        axios.get(`${API}/admin/products`).catch(() => ({ data: mockProducts })),
        axios.get(`${API}/admin/matrices`).catch(() => ({ data: mockMatrices }))
      ]);
      
      setProducts(productsRes.data);
      setMatrices(matricesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load product data');
    } finally {
      setLoading(false);
    }
  };

  // Mock data for demo
  const mockProducts = [
    {
      id: '1',
      name: 'Basic Training Package',
      description: 'Entry level training with PIF system access',
      price: 10.0,
      credits: 1000,
      tier_level: 1,
      phase_requirement: 1,
      lifespan_days: 30,
      is_active: true,
      created_at: new Date().toISOString()
    },
    {
      id: '2',
      name: 'Rookie Pro Package',
      description: 'Phase 2 Level 1 with advanced tools',
      price: 35.0,
      credits: 3500,
      tier_level: 2,
      phase_requirement: 2,
      lifespan_days: 60,
      is_active: true,
      created_at: new Date().toISOString()
    },
    {
      id: '3',
      name: 'Professional Package',
      description: 'Phase 2 Level 2 with premium features',
      price: 100.0,
      credits: 10000,
      tier_level: 3,
      phase_requirement: 2,
      lifespan_days: 90,
      is_active: true,
      created_at: new Date().toISOString()
    }
  ];

  const mockMatrices = [
    { id: '1', name: '1x2 Basic Matrix', matrix_type: '1x2' },
    { id: '2', name: '12x3 Enterprise Matrix', matrix_type: '12x3' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFeatureAdd = () => {
    const feature = prompt('Enter a product feature:');
    if (feature) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, feature]
      }));
    }
  };

  const handleFeatureRemove = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, formData);
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API}/admin/products`, formData);
        toast.success('Product created successfully');
      }
      
      setShowDialog(false);
      setEditingProduct(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to save product:', error);
      const message = error.response?.data?.detail || 'Failed to save product';
      toast.error(message);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description || '',
      price: product.price,
      credits: product.credits,
      matrix_config_id: product.matrix_config_id || '',
      lifespan_days: product.lifespan_days,
      tier_level: product.tier_level || 1,
      phase_requirement: product.phase_requirement || 1,
      prerequisites: product.prerequisites || [],
      graphics_url: product.graphics_url || '',
      features: product.features || [],
      is_active: product.is_active
    });
    setShowDialog(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/products/${productId}`);
      toast.success('Product deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Failed to delete product:', error);
      toast.error('Failed to delete product');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: 10.0,
      credits: 1000,
      matrix_config_id: '',
      lifespan_days: 30,
      tier_level: 1,
      phase_requirement: 1,
      prerequisites: [],
      graphics_url: '',
      features: [],
      is_active: true
    });
  };

  const getTierInfo = (tierLevel) => {
    return tierLevels.find(t => t.value === tierLevel) || tierLevels[0];
  };

  if (loading) {
    return (
      <AdminLayout title="Product Management">
        <div className="flex items-center justify-center h-64">
          <div className="loading-spinner"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Product Management">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Tiered Product System</h2>
          <p className="text-sm text-gray-600">
            Manage products across Entry Level and Phase 2 tiers
          </p>
        </div>
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) {
            resetForm();
            setEditingProduct(null);
          }
        }}>
          <DialogTrigger asChild>
            <Button className="btn-primary">
              <Plus className="h-4 w-4 mr-2" />
              Add Product
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingProduct ? 'Edit Product' : 'Create New Product'}
              </DialogTitle>
              <DialogDescription>
                Configure product details and tier requirements
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Product Name</Label>
                  <Input
                    id="name"
                    placeholder="e.g., Professional Package"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="price">Price ($)</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={(e) => handleInputChange('price', parseFloat(e.target.value))}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Describe what this product includes..."
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="credits">Credits Included</Label>
                  <Input
                    id="credits"
                    type="number"
                    min="0"
                    value={formData.credits}
                    onChange={(e) => handleInputChange('credits', parseInt(e.target.value))}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="lifespan_days">Lifespan (Days)</Label>
                  <Input
                    id="lifespan_days"
                    type="number"
                    min="1"
                    value={formData.lifespan_days}
                    onChange={(e) => handleInputChange('lifespan_days', parseInt(e.target.value))}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="tier_level">Tier Level</Label>
                  <Select 
                    value={formData.tier_level.toString()} 
                    onValueChange={(value) => handleInputChange('tier_level', parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select tier" />
                    </SelectTrigger>
                    <SelectContent>
                      {tierLevels.map((tier) => {
                        const Icon = tier.icon;
                        return (
                          <SelectItem key={tier.value} value={tier.value.toString()}>
                            <div className="flex items-center">
                              <Icon className="h-4 w-4 mr-2" />
                              <div>
                                <div className="font-medium">{tier.label}</div>
                                <div className="text-xs text-gray-500">{tier.description}</div>
                              </div>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phase_requirement">Phase Requirement</Label>
                  <Select 
                    value={formData.phase_requirement.toString()} 
                    onValueChange={(value) => handleInputChange('phase_requirement', parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select phase" />
                    </SelectTrigger>
                    <SelectContent>
                      {phaseRequirements.map((phase) => (
                        <SelectItem key={phase.value} value={phase.value.toString()}>
                          <div>
                            <div className="font-medium">{phase.label}</div>
                            <div className="text-xs text-gray-500">{phase.description}</div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="matrix_config_id">Linked Matrix</Label>
                  <Select 
                    value={formData.matrix_config_id} 
                    onValueChange={(value) => handleInputChange('matrix_config_id', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select matrix (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">No Matrix</SelectItem>
                      {matrices.map((matrix) => (
                        <SelectItem key={matrix.id} value={matrix.id}>
                          {matrix.name} ({matrix.matrix_type})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="graphics_url">Product Graphics URL</Label>
                <div className="flex space-x-2">
                  <Input
                    id="graphics_url"
                    placeholder="https://example.com/product-image.jpg"
                    value={formData.graphics_url}
                    onChange={(e) => handleInputChange('graphics_url', e.target.value)}
                  />
                  <Button type="button" variant="outline" size="sm">
                    <Upload className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Product Features</Label>
                <div className="space-y-2">
                  {formData.features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <Input value={feature} readOnly />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleFeatureRemove(index)}
                        className="text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleFeatureAdd}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Feature
                  </Button>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  id="is_active"
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  className="h-4 w-4 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500"
                />
                <Label htmlFor="is_active" className="text-sm">
                  Product is active and available for purchase
                </Label>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowDialog(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary">
                  {editingProduct ? 'Update Product' : 'Create Product'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tier Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        {tierLevels.map((tier) => {
          const Icon = tier.icon;
          const tierProducts = products.filter(p => p.tier_level === tier.value);
          
          return (
            <Card key={tier.value} className="text-center">
              <CardHeader className="pb-3">
                <div className="w-12 h-12 mx-auto mb-2 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg flex items-center justify-center">
                  <Icon className="h-6 w-6 text-black" />
                </div>
                <CardTitle className="text-sm">{tier.label}</CardTitle>
                <CardDescription className="text-xs">{tier.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-900">{tierProducts.length}</div>
                <p className="text-xs text-gray-500">Products</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Products List */}
      <div className="space-y-6">
        {tierLevels.map((tier) => {
          const tierProducts = products.filter(p => p.tier_level === tier.value);
          const Icon = tier.icon;
          
          if (tierProducts.length === 0) return null;
          
          return (
            <Card key={tier.value}>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Icon className="h-5 w-5 mr-2" />
                  {tier.label} Products
                </CardTitle>
                <CardDescription>
                  {tier.description} • {tierProducts.length} product{tierProducts.length !== 1 ? 's' : ''}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {tierProducts.map((product) => {
                    const tierInfo = getTierInfo(product.tier_level);
                    return (
                      <div key={product.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900">{product.name}</h4>
                            <p className="text-sm text-gray-600 mt-1">{product.description}</p>
                          </div>
                          <Badge className={tierInfo.color}>
                            {tierInfo.label}
                          </Badge>
                        </div>
                        
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Price:</span>
                            <span className="font-medium">${product.price}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Credits:</span>
                            <span className="font-medium">{product.credits.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Lifespan:</span>
                            <span className="font-medium">{product.lifespan_days} days</span>
                          </div>
                          {product.matrix_config_id && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Matrix:</span>
                              <Grid3x3 className="h-4 w-4 text-green-600" />
                            </div>
                          )}
                        </div>
                        
                        <div className="flex space-x-2 mt-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(product)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(product.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* No Products State */}
      {products.length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Products Created</h3>
            <p className="text-gray-600 mb-6">
              Start building your tiered product system with Entry Level and Phase 2 products
            </p>
            <Button className="btn-primary" onClick={() => setShowDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create First Product
            </Button>
          </CardContent>
        </Card>
      )}
    </AdminLayout>
  );
};

export default AdminProducts;